import React, { useRef, useEffect } from 'react';
import { Input } from '../ui/input';

// Drag-to-adjust input component for both mouse and touch
const TouchDragInput = ({ 
  value, 
  onChange, 
  onBlur, 
  step = 0.5, 
  min = 0, 
  max = 100,
  className,
  placeholder,
  label,
  // Validation: pass the function AND params separately to avoid stale closures
  validationFn,      // Function: (currentValue, ...params, direction) => validValue
  validationParams,  // Array of params to pass to validationFn (e.g., [thickness, base])
  dragDirection = 'vertical', // 'vertical' (up=increase) or 'horizontal' (right=increase) or 'horizontal-reverse' (left=increase)
}) => {
  const containerRef = useRef(null);
  const startPos = useRef(null);
  const lastPos = useRef(null);
  const currentValue = useRef(null);
  const isDragging = useRef(false);
  const accumulatedDelta = useRef(0);
  const sensitivity = 12;
  
  // Use refs to always have fresh function and params
  const validationFnRef = useRef(validationFn);
  const validationParamsRef = useRef(validationParams);
  const onChangeRef = useRef(onChange);
  
  // Update refs when props change
  useEffect(() => {
    validationFnRef.current = validationFn;
    validationParamsRef.current = validationParams;
    onChangeRef.current = onChange;
  }, [validationFn, validationParams, onChange]);

  // Calculate direction from delta
  const getDirection = (delta) => {
    if (dragDirection === 'vertical') {
      return delta > 0 ? 1 : -1;
    } else if (dragDirection === 'horizontal') {
      return delta < 0 ? 1 : -1;
    } else if (dragDirection === 'horizontal-reverse') {
      return delta > 0 ? 1 : -1;
    }
    return 0;
  };

  // Handle step increment/decrement - uses refs for fresh values
  const handleStep = (direction) => {
    const actualCurrent = currentValue.current !== null ? currentValue.current : (parseFloat(value) || 0);
    let newValue;
    
    const fn = validationFnRef.current;
    const params = validationParamsRef.current;
    
    if (fn && params) {
      newValue = fn(actualCurrent.toString(), ...params, direction);
    } else {
      newValue = actualCurrent + (direction * step);
      newValue = Math.max(min, Math.min(max, newValue));
      newValue = Math.round(newValue * 10) / 10;
      newValue = newValue.toFixed(1);
    }
    
    currentValue.current = parseFloat(newValue);
    onChangeRef.current(newValue);
  };

  // Process drag movement - called from event listeners
  const processDrag = (pos) => {
    if (startPos.current === null) return;
    
    const totalDelta = Math.abs(startPos.current - pos);
    
    if (totalDelta > 5) {
      isDragging.current = true;
    }
    
    if (!isDragging.current) return;
    
    const delta = lastPos.current - pos;
    accumulatedDelta.current += delta;
    lastPos.current = pos;
    
    // Process ALL accumulated steps using a safe for loop (prevents infinite loop/RangeError)
    const stepsToTake = Math.floor(Math.abs(accumulatedDelta.current) / sensitivity);
    if (stepsToTake > 0) {
      const direction = getDirection(accumulatedDelta.current);
      for (let i = 0; i < stepsToTake; i++) {
        handleStep(direction);
      }
      // Keep the remainder for smooth continuation
      accumulatedDelta.current = accumulatedDelta.current % sensitivity;
    }
  };

  // Track if actual movement happened during mouse down
  const didDrag = useRef(false);

  // Refs for event handlers (to avoid stale closures in document listeners)
  const handleMouseMoveRef = useRef(null);
  const handleMouseUpRef = useRef(null);

  // Touch handlers
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    const pos = dragDirection === 'vertical' ? touch.clientY : touch.clientX;
    startPos.current = pos;
    lastPos.current = pos;
    currentValue.current = parseFloat(value) || 0;
    isDragging.current = false;
    accumulatedDelta.current = 0;
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const pos = dragDirection === 'vertical' ? touch.clientY : touch.clientX;
    processDrag(pos);
    if (isDragging.current) {
      e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    startPos.current = null;
    lastPos.current = null;
    currentValue.current = null;
    isDragging.current = false;
    accumulatedDelta.current = 0;
  };

  // Stable listener functions that delegate to refs
  const mouseMoveListener = (e) => handleMouseMoveRef.current?.(e);
  const mouseUpListener = () => handleMouseUpRef.current?.();

  // Mouse move handler - defined as ref to avoid stale closure
  handleMouseMoveRef.current = (e) => {
    const pos = dragDirection === 'vertical' ? e.clientY : e.clientX;
    processDrag(pos);
    if (isDragging.current) {
      didDrag.current = true;
    }
  };

  // Mouse up handler - defined as ref to avoid stale closure
  handleMouseUpRef.current = () => {
    const wasDrag = didDrag.current;
    const wasValue = currentValue.current;
    
    startPos.current = null;
    lastPos.current = null;
    currentValue.current = null;
    isDragging.current = false;
    accumulatedDelta.current = 0;
    didDrag.current = false;
    
    document.removeEventListener('mousemove', mouseMoveListener);
    document.removeEventListener('mouseup', mouseUpListener);
    
    // If no drag happened, treat as a click - increment by one step
    if (!wasDrag && wasValue !== null) {
      currentValue.current = wasValue;
      handleStep(1);
      currentValue.current = null;
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    const pos = dragDirection === 'vertical' ? e.clientY : e.clientX;
    startPos.current = pos;
    lastPos.current = pos;
    currentValue.current = parseFloat(value) || 0;
    isDragging.current = false;
    accumulatedDelta.current = 0;
    didDrag.current = false;
    
    document.addEventListener('mousemove', mouseMoveListener);
    document.addEventListener('mouseup', mouseUpListener);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', mouseMoveListener);
      document.removeEventListener('mouseup', mouseUpListener);
    };
  }, []);

  // Handle keyboard arrow keys
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      currentValue.current = parseFloat(value) || 0;
      handleStep(1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      currentValue.current = parseFloat(value) || 0;
      handleStep(-1);
    }
  };

  // Get cursor style based on drag direction
  const getCursorClass = () => {
    if (dragDirection === 'vertical') return 'cursor-ns-resize';
    return 'cursor-ew-resize';
  };

  return (
    <div className="flex flex-col items-center">
      <label className="text-xs text-zinc-400 mb-1 print:mb-0 print:text-slate-600">{label}</label>
      {/* Interactive drag input - hidden in print */}
      <div
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        className={`touch-none no-print ${getCursorClass()}`}
      >
        <Input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur ? () => onBlur(value) : undefined}
          onKeyDown={handleKeyDown}
          className={`${className} select-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${getCursorClass()} bg-zinc-800 border-zinc-700 text-zinc-100`}
          placeholder={placeholder}
          step={step}
          min={min}
          max={max}
          readOnly
        />
      </div>
      {/* Static value for print */}
      <div className="hidden print:block text-sm font-semibold text-slate-700">
        {value} mm
      </div>
    </div>
  );
};

export default TouchDragInput;
